import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Button } from '@rocco/components/button';
import { Textarea } from '@rocco/components/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@rocco/components/select';
import { Badge } from '@rocco/components/badge';
import { X, SendIcon, Loader2, Info } from 'lucide-react';
import type { BuildHubCollaborator, BuildHub } from '../../types';
import { cn } from '@rocco/utils/cn';
import { BuildHubRoleEnum } from 'generated/graphql';
import { buildFormDataForCollaboratorsInvite } from '@modules/build-hub/utils/build-collaborators-invite-data';
import { useBuildHubCollaboratorAdd } from '@modules/build-hub/hooks/use-build-hub-collaborator-add';
import { toast } from 'sonner';
import { useUser } from '@modules/user';

interface EmailError {
    email: string;
    message: string;
}

interface InviteCollaboratorsFormProps {
    buildHub: BuildHub;
}

/**
 * Form for inviting new collaborators to a BuildHub project.
 * This component handles email validation, role selection, and invitation sending.
 * Owners can assign all roles (Admin, Editor, Viewer).
 * Admins can assign Editor and Viewer roles (not Admin).
 * Editors can only assign Viewer role (not Editor or Admin).
 */
export const InviteCollaboratorsForm: React.FC<
    InviteCollaboratorsFormProps
> = ({ buildHub }) => {
    const [emails, setEmails] = useState<string[]>([]);
    const [currentInput, setCurrentInput] = useState('');
    const [selectedRole, setSelectedRole] = useState<BuildHubRoleEnum>(
        BuildHubRoleEnum.Viewer
    );
    const [message, setMessage] = useState('');
    const [errors, setErrors] = useState<EmailError[]>([]);
    const [isInputFocused, setIsInputFocused] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const user = useUser();

    // Calculate available roles based on current user's role
    // Users cannot assign roles higher than or equal to their own
    // Owners can assign all roles (Admin, Editor, Viewer)
    const availableRoles = useMemo(() => {
        const currentUserRole = buildHub.currentUserRole;
        const isOwner = String(buildHub.ownerId) === String(user.ID);

        // Owners can assign all roles
        if (isOwner) {
            return [
                BuildHubRoleEnum.Admin,
                BuildHubRoleEnum.Editor,
                BuildHubRoleEnum.Viewer,
            ];
        }

        // If no role, return empty array (shouldn't happen, but safe default)
        if (!currentUserRole) {
            return [];
        }

        // Admins can assign Editor and Viewer roles (not Admin)
        if (currentUserRole === BuildHubRoleEnum.Admin) {
            return [BuildHubRoleEnum.Editor, BuildHubRoleEnum.Viewer];
        }

        // Editors can only assign Viewer role (not Editor or Admin)
        if (currentUserRole === BuildHubRoleEnum.Editor) {
            return [BuildHubRoleEnum.Viewer];
        }

        // Viewers cannot assign any roles
        return [];
    }, [buildHub.currentUserRole, buildHub.ownerId, user.ID]);

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const addEmails = (emailsToAdd: string[]) => {
        const newErrors: EmailError[] = [];
        const validEmails: string[] = [];

        emailsToAdd.forEach((email) => {
            const trimmedEmail = email.trim().toLowerCase();

            if (!trimmedEmail) return;

            if (!validateEmail(trimmedEmail)) {
                newErrors.push({
                    email: trimmedEmail,
                    message: 'Invalid email format',
                });
                return;
            }

            // Check if email already exists in current emails
            if (emails.includes(trimmedEmail)) {
                newErrors.push({
                    email: trimmedEmail,
                    message: 'Email already added',
                });
                return;
            }

            // Check if email is already a collaborator
            if (
                buildHub?.collaborators.some(
                    (c: BuildHubCollaborator) =>
                        c.email.toLowerCase() === trimmedEmail
                )
            ) {
                newErrors.push({
                    email: trimmedEmail,
                    message: 'User already has access',
                });
                return;
            }

            validEmails.push(trimmedEmail);
        });

        setErrors(newErrors);
        if (validEmails.length > 0) {
            setEmails((prev) => [...prev, ...validEmails]);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setCurrentInput(value);

        // Clear errors when user starts typing
        if (errors.length > 0) {
            setErrors([]);
        }

        // Handle comma or space separated emails
        if (value.includes(',') || value.includes(' ')) {
            const emailsToAdd = value
                .split(/[,\s]+/)
                .filter((email) => email.trim());
            if (emailsToAdd.length > 0) {
                addEmails(emailsToAdd);
                setCurrentInput('');
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && currentInput.trim()) {
            e.preventDefault();
            addEmails([currentInput.trim()]);
            setCurrentInput('');
        }

        if (e.key === 'Backspace' && !currentInput && emails.length > 0) {
            setEmails((prev) => prev.slice(0, -1));
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        const emailsToAdd = pastedText
            .split(/[,\s\n]+/)
            .filter((email) => email.trim());
        if (emailsToAdd.length > 0) {
            addEmails(emailsToAdd);
            setCurrentInput('');
        }
    };

    const handleFocus = () => {
        setIsInputFocused(true);
    };

    const handleBlur = () => {
        setIsInputFocused(false);
        if (currentInput.trim()) {
            addEmails([currentInput.trim()]);
            setCurrentInput('');
        }
    };

    const removeEmail = (emailToRemove: string) => {
        setEmails((prev) => prev.filter((email) => email !== emailToRemove));
        // Remove any errors for this email
        setErrors((prev) =>
            prev.filter((error) => error.email !== emailToRemove)
        );
    };

    const onInviteSuccess = useCallback(() => {
        setEmails([]);
        setCurrentInput('');
        setSelectedRole(BuildHubRoleEnum.Viewer);
        setMessage('');
        setErrors([]);
        toast.success('Collaborator added successfully.', {
            action: { label: 'Dismiss', onClick: () => toast.dismiss() },
        });
    }, []);

    const { inviteCollaborators, isUpdating } = useBuildHubCollaboratorAdd({
        onSuccess: onInviteSuccess,
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (emails.length === 0) {
            setErrors([
                { email: '', message: 'Please add at least one email address' },
            ]);
            return;
        }

        try {
            const formData = buildFormDataForCollaboratorsInvite({
                buildHubId: buildHub.id,
                emails,
                role: selectedRole,
                message: message.trim() || undefined,
            });

            inviteCollaborators(formData);
        } catch (error) {
            setErrors([
                {
                    email: '',
                    message: 'Failed to send invitations. Please try again.',
                },
            ]);
        }
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="space-y-4"
            id={'build-hub-collaborators-edit-form'}
        >
            {/* Invite collaborators form */}
            <div>
                <label className="mb-1 block text-base font-medium text-gray-900">
                    Invite collaborators
                </label>
                <p className="mb-4 text-sm text-gray-600">
                    Enter the email addresses of the people you want to invite.
                </p>
                <div className="flex flex-row items-start gap-3">
                    <div className="flex-1">
                        <div
                            className={cn(
                                'flex items-center gap-3 rounded-lg border bg-gray-100 px-2 py-1',
                                errors.length > 0 && 'border-red-400'
                            )}
                        >
                            <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                    {emails.map((email, index) => (
                                        <Badge
                                            key={index}
                                            size="narrow"
                                            color="dark"
                                        >
                                            <span className="flex flex-row items-center gap-1">
                                                {email}
                                                <X
                                                    className="h-3 w-3 cursor-pointer"
                                                    onClick={() =>
                                                        removeEmail(email)
                                                    }
                                                />
                                            </span>
                                        </Badge>
                                    ))}

                                    <input
                                        ref={inputRef}
                                        name="invite-emails"
                                        type="text"
                                        value={currentInput}
                                        onChange={handleInputChange}
                                        onKeyDown={handleKeyDown}
                                        onPaste={handlePaste}
                                        onFocus={handleFocus}
                                        onBlur={handleBlur}
                                        placeholder={
                                            emails.length === 0
                                                ? 'Emails, comma separated'
                                                : ''
                                        }
                                        className="min-w-48 flex-1 border-none bg-transparent px-2 py-2 text-base text-gray-900 outline-none placeholder:text-gray-900/50"
                                    />
                                </div>
                            </div>

                            {emails.length > 0 && (
                                <div>
                                    <Select
                                        value={selectedRole}
                                        onValueChange={(
                                            value: BuildHubRoleEnum
                                        ) => setSelectedRole(value)}
                                        name="invite-role"
                                    >
                                        <SelectTrigger
                                            color="lightOutline"
                                            className="w-28"
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {availableRoles.includes(
                                                BuildHubRoleEnum.Admin
                                            ) && (
                                                <SelectItem
                                                    value={
                                                        BuildHubRoleEnum.Admin
                                                    }
                                                >
                                                    Admin
                                                </SelectItem>
                                            )}
                                            {availableRoles.includes(
                                                BuildHubRoleEnum.Editor
                                            ) && (
                                                <SelectItem
                                                    value={
                                                        BuildHubRoleEnum.Editor
                                                    }
                                                >
                                                    Editor
                                                </SelectItem>
                                            )}
                                            {availableRoles.includes(
                                                BuildHubRoleEnum.Viewer
                                            ) && (
                                                <SelectItem
                                                    value={
                                                        BuildHubRoleEnum.Viewer
                                                    }
                                                >
                                                    Viewer
                                                </SelectItem>
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                        </div>

                        {/* Error display */}
                        {errors.length > 0 && (
                            <div className="mt-2 space-y-1">
                                {errors.map((error, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center gap-1 text-sm text-red-400"
                                    >
                                        {error.email && (
                                            <span className="font-medium">
                                                {error.email}:
                                            </span>
                                        )}
                                        <span>{error.message}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Reminder display */}
                        {selectedRole === BuildHubRoleEnum.Editor &&
                            emails.length > 0 && (
                                <div className="mb-2 mt-2">
                                    <div className="flex items-center gap-1 text-sm font-medium">
                                        <Info className="mr-1 h-4 w-4" />
                                        <span>
                                            Editors can upload files and make
                                            changes to this project.
                                        </span>
                                    </div>
                                </div>
                            )}
                    </div>
                    <div className="pt-1">
                        <Button
                            type="submit"
                            color="dark"
                            disabled={
                                isUpdating ||
                                isInputFocused ||
                                emails.length === 0
                            }
                            className="transition-all duration-200 ease-in-out"
                        >
                            {isUpdating ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <SendIcon className="h-4 w-4" />
                            )}
                            <span className="ml-2">Invite</span>
                        </Button>
                    </div>
                </div>
            </div>

            {/* Message field */}
            <div>
                <label
                    htmlFor="message"
                    className="mb-2 block text-sm font-medium text-gray-700"
                >
                    Custom message (optional)
                </label>

                <Textarea
                    id="message"
                    value={message}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setMessage(e.target.value)
                    }
                    placeholder={`Include a personal message with your invitation.`}
                    rows={3}
                    color="gray"
                    spacing="medium"
                />
            </div>
        </form>
    );
};
